import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Save, ShieldCheck, Download } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import MerchantLogoUploader from "@/components/MerchantLogoUploader";
import api, { formatApiError } from "@/lib/api";

/**
 * Merchant-only profile form: legal info used by the AI
 * to generate compliant game rules.
 */
export default function MerchantProfile({ onRgpdOpen, rgpdAccepted = false }) {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({
    name: "",
    company: "",
    legal_name: "",
    address: "",
    registration_number: "",
    contact_email: "",
    website: "",
    logo_url: null,
    pause_badge: "",
    pause_title: "",
    pause_body: "",
    pause_status: "",
  });
  const [saving, setSaving] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  async function downloadRgpdPdf() {
    setDownloadingPdf(true);
    try {
      const res = await api.get("/merchant/rgpd-contract.pdf", {
        params: { lang: i18n.language?.startsWith("en") ? "en" : "fr" },
        responseType: "blob",
      });
      const cd = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/i.exec(cd);
      const filename = match ? match[1] : "contrat-rgpd-qrise.pdf";
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloadingPdf(false);
    }
  }

  useEffect(() => {
    api
      .get("/merchant/profile")
      .then((r) => setForm((f) => ({ ...f, ...r.data })))
      .catch(() => {});
  }, []);

  async function save() {
    setSaving(true);
    try {
      await api.patch("/merchant/profile", form);
      toast.success(t("profile.saved"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function saveLogo(logo_url) {
    // Persist the logo immediately (independent of the legal-fields save).
    setForm((f) => ({ ...f, logo_url }));
    try {
      await api.patch("/merchant/profile", { logo_url });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  const field = (key, type = "text") => (
    <div>
      <Label className="text-xs font-medium">{t(`profile.${key}`)}</Label>
      <Input
        type={type}
        value={form[key] || ""}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        className="mt-1.5 h-10 rounded-xl"
        data-testid={`profile-${key}`}
      />
    </div>
  );

  return (
    <div className="max-w-2xl">
      <div className="qrise-card p-6 space-y-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground">
            {t("profile.section_display")}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("profile.section_display_hint")}
          </p>
        </div>
        <div>
          <Label className="text-xs font-medium">{t("profile.logo")}</Label>
          <p className="mt-1 mb-2 text-[11px] text-muted-foreground">
            {t("profile.logo_hint")}
          </p>
          <MerchantLogoUploader
            value={form.logo_url}
            onChange={saveLogo}
            size="md"
            testId="profile-logo"
          />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {field("name")}
          {field("company")}
        </div>

        <div className="border-t border-border/60 pt-4">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground">
            {t("profile.section_legal")}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("profile.section_legal_hint")}
          </p>
        </div>
        <div className="grid gap-3">
          {field("legal_name")}
          {field("address")}
          <div className="grid sm:grid-cols-2 gap-3">
            {field("registration_number")}
            {field("contact_email", "email")}
          </div>
          {field("website", "url")}
        </div>

        <div className="border-t border-border/60 pt-4">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-muted-foreground">
            {t("profile.section_pause")}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("profile.section_pause_hint")}
          </p>
        </div>
        <div className="grid gap-3" data-testid="profile-pause-section">
          <div className="grid sm:grid-cols-2 gap-3">
            {field("pause_badge")}
            {field("pause_status")}
          </div>
          {field("pause_title")}
          <div>
            <Label className="text-xs font-medium">{t("profile.pause_body")}</Label>
            <Textarea
              value={form.pause_body || ""}
              onChange={(e) => setForm({ ...form, pause_body: e.target.value })}
              rows={3}
              className="mt-1.5 rounded-xl"
              data-testid="profile-pause_body"
            />
          </div>
        </div>

        <div className="flex justify-end pt-2">
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-xl h-10 bg-foreground text-background hover:bg-foreground/90"
            data-testid="profile-save-btn"
          >
            <Save className="h-4 w-4 mr-1.5" />
            {saving ? "…" : t("common.save")}
          </Button>
        </div>
      </div>

      {onRgpdOpen && (
        <div className="qrise-card p-6 mt-6 flex items-start gap-4 flex-wrap">
          <div className="h-10 w-10 rounded-xl bg-foreground/5 border border-border flex items-center justify-center shrink-0">
            <ShieldCheck className="h-4 w-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold">
              {t("rgpd.card_title")}
            </div>
            <p className="mt-1 text-[13px] text-muted-foreground">
              {t("rgpd.card_hint")}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              onClick={onRgpdOpen}
              className="rounded-xl h-9 text-xs"
              data-testid="profile-open-rgpd-btn"
            >
              <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />
              {t("rgpd.card_cta")}
            </Button>
            {rgpdAccepted && (
              <Button
                onClick={downloadRgpdPdf}
                disabled={downloadingPdf}
                className="rounded-xl h-9 text-xs bg-foreground text-background hover:bg-foreground/90"
                data-testid="profile-download-rgpd-pdf-btn"
              >
                <Download className="h-3.5 w-3.5 mr-1.5" />
                {downloadingPdf ? "…" : t("rgpd.download_pdf")}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
